import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _get from 'lodash/get';

import COLORS from '@mlb/react-patterns/style/universal.color';
import Button from '@mlb/react-patterns/components/Button/button';
import { TitleWrapper, Metadata } from '@mlb/react-patterns/components/ContentCard/contentCard.style';
import Icon from '@mlb/react-patterns/components/Icon/icon';
import { IconWrapper } from '@mlb/react-patterns/components/Icon/icon.style.tsx';

import { Tracking } from 'bdvp-tracking';
import { useClipTrimming } from 'bdvp-common/src/hooks';

import ReelCard  from './ReelCard';

const CARD_WRAPPER_WIDTH = 230;
const CARD_WRAPPER_BORDER = 5;

export const CardWrapper = styled.div`
    position: relative;
    width: ${CARD_WRAPPER_WIDTH}px;
    display: inline-block;
    margin-right: 8px;
    white-space: normal;
    border-radius: 6px;
    background-color: ${COLORS.mlbGrey7};
    border: ${CARD_WRAPPER_BORDER}px solid ${COLORS.transparent};

    ${({ isDragging }) => isDragging && `
        border-color: ${COLORS.mlbGrey7};
        box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.4);
        z-index: 10001 !important;
    `};
`;

const Card = styled(ReelCard)`
    line-height: normal;
    font-weight: 500;

    ${Metadata} p {
        font-size: 12px;
        letter-spacing: normal;
        color: ${COLORS.mlbGrey4};
        margin-top: 10px;
    }

    ${TitleWrapper} {
        font-size: 14px;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        max-height: 34px;
    }
`;

const DragHandle = styled.button`
    margin: -1px 0 0 10px;
    height: fit-content;
    cursor: move; /* fallback if grab cursor is unsupported */
    cursor: grab;
    cursor: -moz-grab;
    cursor: -webkit-grab;
    padding: 0;
    border: none;
    background-color: ${COLORS.transparent};
`;

const DragIcon = styled(Icon)`
    pointer-events: all;
    stroke: none;
`;

const Btn = styled(Button)`
    position: absolute;
    top: 10px;
    width: 24px;
    height: 24px;
    padding: 0;
    background: rgba(0,0,0,0.75);

    ${IconWrapper} {
        stroke: none;
    }
`;

const RemoveBtn = styled(Btn)`
    right: 10px;
`;

const TrimBtn = styled(Btn)`
    left: 10px;
`;

const PopoverCard = forwardRef(({
    openClipTrimming,
    removeClip,
    mediaPlayback,
    draggableProps,
    dragHandleProps,
    config,
    lang,
    isDragging,
    dictionary,
    reel,
}, ref) => {

    const { canTrim } = useClipTrimming(mediaPlayback, config);

    return (
        <CardWrapper
            ref={ref}
            {...draggableProps}
            {...dragHandleProps}
            isDragging={isDragging}
        >
            <Card
                orientation={'portrait'}
                mediaPlayback={mediaPlayback}
                config={config}
                lang={lang}
                optionsMenu={
                    <DragHandle
                        aria-label={_get(dictionary, 'reorder_video', 'Reorder video')}
                    >
                        <DragIcon
                            name={'swap'}
                            width={22}
                            fill={COLORS.mlbWhite}
                        />
                    </DragHandle>
                }
                reel={reel}
                imgSizes={`${CARD_WRAPPER_WIDTH - (CARD_WRAPPER_BORDER * 2)}px`}
            />
            <RemoveBtn
                iconName={'close'}
                styleName={'default'}
                shape={'pill'}
                iconSize={12}
                size={'sm'}
                hasHover={false}
                handlers={{
                    onClick: (event) => {

                        // This is needed to stop the reels cart container from closing on clip removal
                        event.stopPropagation();

                        Tracking.trackRemoveReelClick();
                        removeClip(mediaPlayback.id);

                    },
                }}
            />
            {
                canTrim
                && <TrimBtn
                    iconName={'scissors'}
                    styleName={'default'}
                    shape={'pill'}
                    iconSize={14}
                    size={'sm'}
                    hasHover={false}
                    handlers={{
                        onClick: () => {

                            Tracking.trackReelCreationOpenClipTrimmingModal();
                            openClipTrimming(mediaPlayback.id);

                        },
                    }}
                />
            }
        </CardWrapper>
    );

});

PopoverCard.propTypes = {
    openClipTrimming: PropTypes.func.isRequired,
    removeClip: PropTypes.func.isRequired,
    mediaPlayback: PropTypes.object.isRequired,
    draggableProps: PropTypes.object.isRequired,
    dragHandleProps: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired,
    lang: PropTypes.string.isRequired,
    isDragging: PropTypes.bool.isRequired,
    dictionary: PropTypes.object.isRequired,
    reel: PropTypes.object.isRequired,
};

export default PopoverCard;
